import { NormalizedNumber, Percentage } from '@sparkdotfi/common-universal'
import { ReactNode } from 'react'

import { formatPercentage } from '@/domain/common/format'
import { Token } from '@/domain/types/Token'
import { Button } from '@/ui/atoms/button/Button'
import { Panel } from '@/ui/atoms/panel/Panel'
import { GrowingReward } from '@/ui/molecules/growing-reward/GrowingReward'
import { Info } from '@/ui/molecules/info/Info'
import { cn } from '@/ui/utils/style'
import { testIds } from '@/ui/utils/testIds'

export interface ActiveFarmPanelProps {
  canClaim: boolean
  stakedAmount: NormalizedNumber
  rewardToken: Token
  stakingToken: Token
  calculateReward: (timestampInMs: number) => NormalizedNumber
  refreshGrowingRewardIntervalInMs: number | undefined
  openClaimDialog: () => void
  openUnstakeDialog: () => void
  openStakeDialog: () => void
  apy: Percentage
}

export function ActiveFarmPanel({
  canClaim,
  stakedAmount,
  rewardToken,
  stakingToken,
  calculateReward,
  refreshGrowingRewardIntervalInMs,
  openClaimDialog,
  openUnstakeDialog,
  openStakeDialog,
  apy,
}: ActiveFarmPanelProps) {
  return (
    <Panel
      spacing="m"
      className={cn(
        'flex flex-col justify-between gap-8 lg:gap-16',
        'bg-cover bg-right bg-no-repeat',
        'bg-[url(/src/ui/assets/spark-staking/staking-rewards-panel-bg.svg)] bg-primary-inverse',
      )}
    >
      <div className="flex w-full flex-row items-center justify-between">
        <div className="typography-heading-4 flex items-baseline gap-1 text-primary-inverse">My Rewards</div>
        <div className="grid grid-cols-[80px_80px] gap-1">
          {canClaim && (
            <Button
              onClick={openClaimDialog}
              variant="primary"
              size="m"
              data-testid={testIds.spkFarm.activeFarmPanel.claimButton}
            >
              Claim
            </Button>
          )}
          {(!canClaim || stakedAmount.eq(0)) && (
            <Button
              onClick={openStakeDialog}
              variant={canClaim ? 'secondary' : 'primary'}
              size="m"
              data-testid={testIds.spkFarm.activeFarmPanel.stakeButton}
            >
              Deposit
            </Button>
          )}
          {stakedAmount.gt(0) && (
            <Button
              onClick={openUnstakeDialog}
              variant="secondary"
              size="m"
              data-testid={testIds.spkFarm.activeFarmPanel.unstakeButton}
            >
              Withdraw
            </Button>
          )}
        </div>
      </div>
      <GrowingReward
        rewardIconPath={rewardToken.logo}
        rewardToken={rewardToken}
        calculateReward={calculateReward}
        refreshIntervalInMs={refreshGrowingRewardIntervalInMs}
        wholePartTextBgGradientClass={cn('bg-gradient-spark-primary-2')}
        fractionalPartTextColorClass="text-[#FF63D0]"
        rewardsTextDataTestId={testIds.spkFarm.activeFarmPanel.rewards}
        rewardsUsdTextDataTestId={testIds.spkFarm.activeFarmPanel.rewardsUsd}
      />
      <div className="flex divide-x divide-fg-secondary">
        <DetailsItem label="My Deposit">
          <div
            className="typography-label-1 flex items-center gap-1.5 text-primary-inverse"
            data-testid={testIds.spkFarm.activeFarmPanel.staked}
          >
            <img src={stakingToken.logo} className="h-4" alt={stakingToken.symbol} />
            {stakingToken.format(stakedAmount, { style: 'auto' })}
          </div>
        </DetailsItem>
        <DetailsItem
          label="APY"
          tooltip="The yield comes from Sky Token Rewards offered by Sky. The APY is calculated based on three factors: your share of the total deposits, the token emissions, and the current price of the reward token."
        >
          <div className="typography-label-1 bg-gradient-spark-primary-2 bg-clip-text text-transparent">
            {formatPercentage(apy, { minimumFractionDigits: 0 })}
          </div>
        </DetailsItem>
      </div>
    </Panel>
  )
}

function DetailsItem({
  label,
  tooltip,
  children,
  className,
}: {
  label: string
  tooltip?: string
  children: ReactNode
  className?: string
}) {
  return (
    <div className={cn('flex flex-col gap-1 px-3 first:pl-0 last:pr-0 xl:px-6', className)}>
      <div className="typography-label-3 flex items-center gap-1 text-tertiary">
        {label}
        {tooltip && <Info className="icon-secondary">{tooltip}</Info>}
      </div>
      <div className="typography-label-1 flex items-center gap-1.5 text-primary-inverse">{children}</div>
    </div>
  )
}
